import React, { useState, useEffect, useCallback } from 'react';

interface DemoReaderProps {
  isActive: boolean;
  wpm?: number;
  text?: string;
  onComplete?: () => void;
  countdownWord?: string | null;
}

const DEMO_TEXTS = [
  "The quick brown fox jumps over the lazy dog",
  "Reading at 600 WPM feels like magic",
  "Your brain adapts faster than you think",
  "Speed reading is a skill you can master",
  "Focus and practice are all you need"
];

const DemoReader: React.FC<DemoReaderProps> = ({
  isActive,
  wpm = 300,
  text,
  onComplete,
  countdownWord
}) => {
  const [currentText, setCurrentText] = useState(text || DEMO_TEXTS[0]);
  const [words, setWords] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Split text into words
  useEffect(() => {
    const demoText = text || currentText;
    const wordList = demoText.split(/\s+/).filter(w => w.length > 0);
    setWords(wordList);
    setCurrentIndex(0);
  }, [currentText, text]);

  // Calculate interval based on WPM
  const intervalMs = wpm > 0 ? (60 * 1000) / wpm : 1000;

  // Handle playback
  useEffect(() => {
    if (!isActive || !isPlaying) return;

    const timer = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= words.length - 1) {
          setIsPlaying(false);
          onComplete?.();
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);

    return () => clearInterval(timer);
  }, [isActive, isPlaying, intervalMs, words.length, onComplete]);

  // Start playing when activated (but not during countdown)
  useEffect(() => {
    if (isActive && words.length > 0 && !countdownWord) {
      setIsPlaying(true);
    }
  }, [isActive, words.length, countdownWord]);

  // Get current word with ORP highlighting
  const getDisplayWord = useCallback(() => {
    if (words.length === 0 || currentIndex >= words.length) {
      return { left: '', center: '', right: '' };
    }

    const word = words[currentIndex];
    const centerIndex = Math.floor(word.length / 2);
    
    return {
      left: word.slice(0, centerIndex),
      center: word[centerIndex] || '',
      right: word.slice(centerIndex + 1)
    };
  }, [words, currentIndex]);

  const displayWord = getDisplayWord();

  if (!isActive) return null;

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* RSVP Display */}
      <div className="relative w-full h-48 md:h-56 bg-slate-100 dark:bg-slate-900 rounded-2xl shadow-2xl border-2 border-slate-300 dark:border-slate-600 flex items-center justify-center overflow-hidden mb-6">
        {/* Fixed Vertical Guides */}
        <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[2px] h-[15%] bg-slate-300 dark:bg-slate-700/50"></div>
        <div className="absolute bottom-[20%] left-1/2 -translate-x-1/2 w-[2px] h-[15%] bg-slate-300 dark:bg-slate-700/50"></div>

        {/* The Word Container */}
        <div className="w-full flex items-baseline justify-center font-mono text-5xl md:text-6xl font-bold tracking-wide">
          {countdownWord ? (
            <>
              <div className="flex-1 text-right text-slate-900 dark:text-white whitespace-pre">
                {countdownWord.includes(' ') ? countdownWord.split(' ')[0] + ' ' : countdownWord.slice(0, Math.floor(countdownWord.length / 2))}
              </div>
              <div className="text-red-500 mx-[1px] relative z-10">
                {countdownWord.includes(' ') ? countdownWord.split(' ')[1][0] : countdownWord[Math.floor(countdownWord.length / 2)]}
              </div>
              <div className="flex-1 text-left text-slate-900 dark:text-white whitespace-pre">
                {countdownWord.includes(' ') ? countdownWord.split(' ').slice(1).join(' ').slice(1) : countdownWord.slice(Math.floor(countdownWord.length / 2) + 1)}
              </div>
            </>
          ) : words.length > 0 && currentIndex < words.length ? (
            <>
              <div className="flex-1 text-right text-slate-900 dark:text-white whitespace-pre">
                {displayWord.left}
              </div>
              <div className="text-red-500 mx-[1px] relative z-10">
                {displayWord.center}
              </div>
              <div className="flex-1 text-left text-slate-900 dark:text-white whitespace-pre">
                {displayWord.right}
              </div>
            </>
          ) : (
            <span className="text-slate-400 dark:text-slate-500 text-3xl">Ready?</span>
          )}
        </div>
      </div>

      {/* WPM Display */}
      <div className="text-center">
        <span className="inline-block px-4 py-2 bg-primary/20 text-primary dark:text-blue-400 rounded-full text-sm font-semibold">
          {wpm} WPM
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full mt-4 overflow-hidden">
        <div 
          className="h-full bg-primary rounded-full transition-all duration-100 ease-linear"
          style={{ width: `${words.length > 0 ? ((currentIndex + 1) / words.length) * 100 : 0}%` }}
        />
      </div>

      {/* Word Counter */}
      <div className="text-center mt-2 text-slate-500 dark:text-slate-400 text-sm font-mono">
        {currentIndex + 1} / {words.length}
      </div>
    </div>
  );
};

export default DemoReader;
